'use client';

import React, { useState, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import UserManual from '@/components/layout/UserManual';
import { BannerDisplay } from '@/components/layout/BannerDisplay';

interface TaxBracket {
  rate: number;
  threshold: number;
}

interface TaxResult {
  grossIncome: number;
  taxableIncome: number;
  federalTax: number;
  stateTax: number;
  takeHomePay: number;
  effectiveRate: number;
  marginalRate: number;
  taxBreakdown: Array<{
    bracket: number;
    rate: number;
    amount: number;
    tax: number;
  }>;
}

// 2023 Tax Brackets for Single Filers
const SINGLE_TAX_BRACKETS: TaxBracket[] = [
  { rate: 0.10, threshold: 0 },
  { rate: 0.12, threshold: 11000 },
  { rate: 0.22, threshold: 44725 },
  { rate: 0.24, threshold: 95375 },
  { rate: 0.32, threshold: 182100 },
  { rate: 0.35, threshold: 231250 },
  { rate: 0.37, threshold: 578125 },
];

// 2023 Tax Brackets for Married Filing Jointly
const MARRIED_TAX_BRACKETS: TaxBracket[] = [
  { rate: 0.10, threshold: 0 },
  { rate: 0.12, threshold: 22000 },
  { rate: 0.22, threshold: 89450 },
  { rate: 0.24, threshold: 190750 },
  { rate: 0.32, threshold: 364200 },
  { rate: 0.35, threshold: 462500 },
  { rate: 0.37, threshold: 693750 },
];

// 2023 Tax Brackets for Head of Household
const HEAD_TAX_BRACKETS: TaxBracket[] = [
  { rate: 0.10, threshold: 0 },
  { rate: 0.12, threshold: 15700 },
  { rate: 0.22, threshold: 59850 },
  { rate: 0.24, threshold: 95350 },
  { rate: 0.32, threshold: 182100 },
  { rate: 0.35, threshold: 231250 },
  { rate: 0.37, threshold: 578100 },
];

// Move formatting functions outside component
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatPercentage = (value: number) => {
  return `${value.toFixed(1)}%`;
};

const TaxCalculator: React.FC = () => {
  const t = useTranslations('tax');
  const commonT = useTranslations('common');
  const [grossIncome, setGrossIncome] = useState<number>(75000);
  const [itemizedDeductions, setItemizedDeductions] = useState<number>(12500);
  const [filingStatus, setFilingStatus] = useState<string>('single');
  const [dependents, setDependents] = useState<number>(0);
  const [stateRate, setStateRate] = useState<number>(5);
  const [result, setResult] = useState<TaxResult | null>(null);

  // Add focus state for each input
  const [isGrossIncomeFocused, setIsGrossIncomeFocused] = useState(false);
  const [isItemizedDeductionsFocused, setIsItemizedDeductionsFocused] = useState(false);
  const [isDependentsFocused, setIsDependentsFocused] = useState(false);
  const [isStateRateFocused, setIsStateRateFocused] = useState(false);

  // Add touched state for each input
  const [grossIncomeTouched, setGrossIncomeTouched] = useState(false);
  const [itemizedDeductionsTouched, setItemizedDeductionsTouched] = useState(false);
  const [dependentsTouched, setDependentsTouched] = useState(false);
  const [stateRateTouched, setStateRateTouched] = useState(false);

  const manualSteps = [
    {
      label: t('manual.steps.income.title'),
      content: () => t('manual.steps.income.description')
    },
    {
      label: t('manual.steps.status.title'),
      content: () => t('manual.steps.status.description')
    },
    {
      label: t('manual.steps.dependents.title'),
      content: () => t('manual.steps.dependents.description')
    },
    {
      label: t('manual.steps.deductions.title'),
      content: () => t('manual.steps.deductions.description')
    },
    {
      label: t('manual.steps.results.title'),
      content: () => t('manual.steps.results.description')
    }
  ];


  const manualTips = [
    t('manual.tips.deductions'),
    t('manual.tips.tracking'),
    t('manual.tips.withholding'),
    t('manual.tips.professional')
  ].map(tip => ({
    title: '',
    description: tip
  }));

  const getStandardDeduction = useCallback(() => {
    switch (filingStatus) {
      case 'married':
        return 27700;
      case 'head':
        return 20800;
      default:
        return 13850;
    }
  }, [filingStatus]);

  const getTaxBrackets = useCallback(() => {
    switch (filingStatus) {
      case 'married':
        return MARRIED_TAX_BRACKETS;
      case 'head':
        return HEAD_TAX_BRACKETS;
      default:
        return SINGLE_TAX_BRACKETS;
    }
  }, [filingStatus]);

  const calculateTax = useCallback(() => {
    const standardDeduction = getStandardDeduction();
    const totalDeductions = Math.max(standardDeduction, itemizedDeductions) + (dependents * 2000);
    const taxableIncome = Math.max(0, grossIncome - totalDeductions);
    const taxBrackets = getTaxBrackets();
    const taxBreakdown = [];
    let totalFederalTax = 0;
    let marginalRate = 0;

    // Calculate federal tax for each bracket
    for (let i = 0; i < taxBrackets.length; i++) {
      const currentBracket = taxBrackets[i];
      const nextBracket = taxBrackets[i + 1];
      const bracketThreshold = currentBracket.threshold;
      const nextThreshold = nextBracket ? nextBracket.threshold : Infinity;

      if (taxableIncome > bracketThreshold) {
        const taxableInThisBracket = Math.min(
          taxableIncome - bracketThreshold,
          nextThreshold - bracketThreshold
        );
        const taxInThisBracket = taxableInThisBracket * currentBracket.rate;
        totalFederalTax += taxInThisBracket;

        taxBreakdown.push({
          bracket: i + 1,
          rate: currentBracket.rate * 100,
          amount: taxableInThisBracket,
          tax: taxInThisBracket,
        });

        if (taxableIncome > bracketThreshold && taxableIncome <= nextThreshold) {
          marginalRate = currentBracket.rate * 100;
        }
      }
    }

    // Calculate state tax (simplified)
    const stateTax = (taxableIncome * stateRate) / 100;

    // Calculate effective rate and take-home pay
    const totalTax = totalFederalTax + stateTax;
    const effectiveRate = (totalTax / grossIncome) * 100;
    const takeHomePay = grossIncome - totalTax;

    setResult({
      grossIncome,
      taxableIncome,
      federalTax: totalFederalTax,
      stateTax,
      takeHomePay,
      effectiveRate,
      marginalRate,
      taxBreakdown,
    });
  }, [grossIncome, itemizedDeductions, dependents, stateRate, getStandardDeduction, getTaxBrackets]);

  return (
    <>
      <Container maxWidth="lg">
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label={t('labels.annual_income')}
              type="number"
              value={isGrossIncomeFocused && !grossIncomeTouched ? '' : (isNaN(grossIncome) ? '' : grossIncome)}
              onFocus={() => setIsGrossIncomeFocused(true)}
              onBlur={(e) => {
                setIsGrossIncomeFocused(false);
                setGrossIncomeTouched(false);
                const value = Number(e.target.value);
                if (!e.target.value) {
                  setGrossIncome(75000); // default
                } else if (value < 0) {
                  setGrossIncome(0);
                } else if (value > 10000000) {
                  setGrossIncome(10000000);
                } else {
                  setGrossIncome(value);
                }
              }}
              onChange={(e) => {
                setGrossIncomeTouched(true);
                setGrossIncome(e.target.value === '' ? NaN : Number(e.target.value));
              }}
              slotProps={{
                input: {
                  startAdornment: '$',
                  inputProps: {
                    min: 0,
                    max: 10000000,
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label={t('labels.itemized_deductions')}
              type="number"
              value={isItemizedDeductionsFocused && !itemizedDeductionsTouched ? '' : (isNaN(itemizedDeductions) ? '' : itemizedDeductions)}
              onFocus={() => setIsItemizedDeductionsFocused(true)}
              onBlur={(e) => {
                setIsItemizedDeductionsFocused(false);
                setItemizedDeductionsTouched(false);
                const value = Number(e.target.value);
                if (!e.target.value) {
                  setItemizedDeductions(12500); // default
                } else if (value < 0) {
                  setItemizedDeductions(0);
                } else if (value > 10000000) {
                  setItemizedDeductions(10000000);
                } else {
                  setItemizedDeductions(value);
                }
              }}
              onChange={(e) => {
                setItemizedDeductionsTouched(true);
                setItemizedDeductions(e.target.value === '' ? NaN : Number(e.target.value));
              }}
              slotProps={{
                input: {
                  startAdornment: '$',
                  inputProps: {
                    min: 0,
                    max: 10000000,
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label={t('labels.dependents')}
              type="number"
              value={isDependentsFocused && !dependentsTouched ? '' : (isNaN(dependents) ? '' : dependents)}
              onFocus={() => setIsDependentsFocused(true)}
              onBlur={(e) => {
                setIsDependentsFocused(false);
                setDependentsTouched(false);
                const value = Number(e.target.value);
                if (!e.target.value) {
                  setDependents(0); // default
                } else if (value < 0) {
                  setDependents(0);
                } else if (value > 20) {
                  setDependents(20);
                } else {
                  setDependents(value);
                }
              }}
              onChange={(e) => {
                setDependentsTouched(true);
                setDependents(e.target.value === '' ? NaN : Number(e.target.value));
              }}
              slotProps={{
                input: {
                  inputProps: {
                    min: 0,
                    max: 20,
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label={t('labels.state_tax_rate')}
              type="number"
              value={isStateRateFocused && !stateRateTouched ? '' : (isNaN(stateRate) ? '' : stateRate)}
              onFocus={() => setIsStateRateFocused(true)}
              onBlur={(e) => {
                setIsStateRateFocused(false);
                setStateRateTouched(false);
                const value = Number(e.target.value);
                if (!e.target.value) {
                  setStateRate(5); // default
                } else if (value < 0) {
                  setStateRate(0);
                } else if (value > 100) {
                  setStateRate(100);
                } else {
                  setStateRate(value);
                }
              }}
              onChange={(e) => {
                setStateRateTouched(true);
                setStateRate(e.target.value === '' ? NaN : Number(e.target.value));
              }}
              slotProps={{
                input: {
                  endAdornment: '%',
                  inputProps: {
                    min: 0,
                    max: 100,
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            <FormControl fullWidth>
              <InputLabel>{t('labels.filing_status')}</InputLabel>
              <Select
                value={filingStatus}
                label={t('labels.filing_status')}
                onChange={(e) => setFilingStatus(e.target.value)}
              >
                <MenuItem value="single">{t('filing_status_options.single')}</MenuItem>
                <MenuItem value="married">{t('filing_status_options.married')}</MenuItem>
                <MenuItem value="head">{t('filing_status_options.head')}</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ gridColumn: { xs: '1 / -1' } }}>
              <Button
                variant="contained"
                color="primary"
                onClick={calculateTax}
                fullWidth
              >
                {commonT('calculate')}
              </Button>
            </Box>
          </Box>

          {result && (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('labels.federal_tax')}
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {formatCurrency(result.federalTax)}
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('labels.state_tax')}
                    </Typography>
                    <Typography variant="h4" color="secondary">
                      {formatCurrency(result.stateTax)}
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('labels.take_home_pay')}
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {formatCurrency(result.takeHomePay)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              <Paper sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ p: 2 }}>
                  {t('labels.tax_breakdown')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('labels.tax_type')}</TableCell>
                        <TableCell align="right">{t('labels.amount')}</TableCell>
                        <TableCell align="right">{t('labels.percentage')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{t('labels.gross_income')}</TableCell>
                        <TableCell align="right">{formatCurrency(result.grossIncome)}</TableCell>
                        <TableCell align="right">100%</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t('labels.taxable_income')}</TableCell>
                        <TableCell align="right">{formatCurrency(result.taxableIncome)}</TableCell>
                        <TableCell align="right">{formatPercentage((result.taxableIncome / result.grossIncome) * 100)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t('labels.federal_tax')}</TableCell>
                        <TableCell align="right">{formatCurrency(result.federalTax)}</TableCell>
                        <TableCell align="right">{formatPercentage((result.federalTax / result.grossIncome) * 100)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t('labels.state_tax')}</TableCell>
                        <TableCell align="right">{formatCurrency(result.stateTax)}</TableCell>
                        <TableCell align="right">{formatPercentage((result.stateTax / result.grossIncome) * 100)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t('labels.take_home_pay')}</TableCell>
                        <TableCell align="right">{formatCurrency(result.takeHomePay)}</TableCell>
                        <TableCell align="right">{formatPercentage((result.takeHomePay / result.grossIncome) * 100)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Paper sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ p: 2 }}>
                  {t('labels.federal_tax_brackets')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('labels.bracket')}</TableCell>
                        <TableCell align="right">{t('labels.rate')}</TableCell>
                        <TableCell align="right">{t('labels.taxable_amount')}</TableCell>
                        <TableCell align="right">{t('labels.tax_amount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.taxBreakdown.map((row) => (
                        <TableRow key={row.bracket}>
                          <TableCell>{row.bracket}</TableCell>
                          <TableCell align="right">{formatPercentage(row.rate)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.tax)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}
        </Paper>
        <UserManual 
          steps={manualSteps}
          tips={manualTips}
        />
        <BannerDisplay pageType="tax_calculator" />
      </Container>
    </>
  );
};

export default TaxCalculator; 